import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { GalleryTile } from "@/components/home/GalleryPreview";
import { cn } from "@/lib/utils";
import { EASE } from "@/components/motion/Reveal";

export default function Gallery() {
  const { gallery: items } = useOutletContext();
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);
  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const shown = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="gallery-page">
      <PageHeader eyebrow="Gallery" lines={["Inside", "West Canal Motors."]} sub="The showroom, the workshop, the parts counter and the cars themselves.">
        <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`} className={cn("rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300", filter === c ? "border-crimson bg-crimson text-white" : "border-white/15 text-slate-300 hover:border-white/50")}>
              {c}
            </button>
          ))}
        </div>
      </PageHeader>
      <section className="wrap pb-24 sm:pb-32">
        <motion.div layout className="grid grid-cols-2 gap-3 auto-rows-[200px] md:grid-cols-4 md:auto-rows-[260px]">
          <AnimatePresence mode="popLayout">
            {shown.map((item, i) => (
              <motion.div key={item.id} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.5, ease: EASE }} className={cn(i % 5 === 0 && "col-span-2 row-span-2")}>
                <GalleryTile item={item} onClick={() => setOpen(item)} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {!shown.length && <p className="py-20 text-center text-slate-500">No images in this category yet.</p>}
      </section>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="gallery-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.2 } }} onClick={() => setOpen(null)} className="fixed inset-0 z-[90] flex items-center justify-center bg-obsidian/95 p-6 backdrop-blur-xl">
            <button data-testid="gallery-lightbox-close" className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white hover:border-white" onClick={() => setOpen(null)} aria-label="Close"><X className="h-5 w-5" /></button>
            <motion.figure initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, transition: { duration: 0.2 } }} transition={{ duration: 0.6, ease: EASE }} className="max-h-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
              <img src={open.image} alt={open.title} className="max-h-[80vh] w-auto object-contain" />
              <figcaption className="mt-4 flex items-center gap-4"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">{open.category}</span><span className="font-display text-lg">{open.title}</span></figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
